// ================================================================================================================== //
// Misc Objects                                                                                                       //
// ================================================================================================================== //
export var ComponentType;
(function (ComponentType) {
    ComponentType["U8"] = "U8";
    ComponentType["U16"] = "U16";
    ComponentType["U32"] = "U32";
    ComponentType["U64"] = "U64";
    ComponentType["I8"] = "I8";
    ComponentType["I16"] = "I16";
    ComponentType["I32"] = "I32";
    ComponentType["I64"] = "I64";
    ComponentType["F32"] = "F32";
    ComponentType["F64"] = "F64";
})(ComponentType || (ComponentType = {}));
export var CameraProjection;
(function (CameraProjection) {
    /**
     * Perspective view with variable depth depending on the distance to
     * the camera.
     */
    CameraProjection[CameraProjection["Perspective"] = 0] = "Perspective";
    /**
     * Orthographic view with a fixed depth regardless of the distance to
     * the camera.
     */
    CameraProjection[CameraProjection["Orthographic"] = 1] = "Orthographic";
})(CameraProjection || (CameraProjection = {}));
/**
 * Abstract interface for an IFontAsset object. A font asset represents a loaded .ttf/.otf/.woff file that can be used
 * for rendering text in 3D. Font assets also own an internal glyph cache that keeps a texture atlas of all unique
 * glyphs needed by any of its users.
 *
 * Making multiple font assets of the same file *will* create duplicate resources for fundamentally the same data.
 */
export class IFontAsset {
}
// ================================================================================================================== //
// Style                                                                                                              //
// ================================================================================================================== //
export var PointCloudStyle;
(function (PointCloudStyle) {
    /**
     * No styling is applied and the direct feature colors are used
     */
    PointCloudStyle[PointCloudStyle["None"] = 0] = "None";
    /**
     * Visualize the Intensity of the points as the color of the points
     */
    PointCloudStyle[PointCloudStyle["IntensityColoring"] = 1] = "IntensityColoring";
    /**
     * Visualize the Classification of the points as the color of the points
     */
    PointCloudStyle[PointCloudStyle["ClassificationColoring"] = 2] = "ClassificationColoring";
    /**
     * Visualize a color gradient of the points
     */
    PointCloudStyle[PointCloudStyle["GradientColoring"] = 3] = "GradientColoring";
})(PointCloudStyle || (PointCloudStyle = {}));
// ================================================================================================================== //
// Material                                                                                                           //
// ================================================================================================================== //
/**
 * Enumeration of all supported face culling modes
 */
export var FaceCullingMode;
(function (FaceCullingMode) {
    /**
     * Cull back faces
     */
    FaceCullingMode[FaceCullingMode["BackFace"] = 0] = "BackFace";
    /**
     * Cull front faces
     */
    FaceCullingMode[FaceCullingMode["FrontFace"] = 1] = "FrontFace";
    /**
     * Do not perform any culling
     */
    FaceCullingMode[FaceCullingMode["None"] = 2] = "None";
})(FaceCullingMode || (FaceCullingMode = {}));
/**
 * Enumeration of all supported render layers
 */
export var RenderLayer;
(function (RenderLayer) {
    /**
     * The main render layer, where the majority of objects will be rendered.
     */
    RenderLayer[RenderLayer["Main"] = 0] = "Main";
    /**
     * This render layer occurs after the Main layer, as well as after the panoramas are composited. This layer exists
     * to allow rendering objects on top of the panorama.
     */
    RenderLayer[RenderLayer["AfterPanorama"] = 1] = "AfterPanorama";
})(RenderLayer || (RenderLayer = {}));
/// The well-known name of the 'intensity' property on point cloud tiles. Use with ITileset.pointChannels to query the
/// presence of intensity data.
export const IntensityName = "Intensity";
/// The well-known name of the 'classification' property on point cloud tiles. Use with ITileset.pointChannels to query
/// the presence of classification data.
export const ClassificationName = "Classification";
/**
 * Enumeration of all distinct object types
 */
export var WorldObjectType;
(function (WorldObjectType) {
    /**
     * A box object created with IObjectFactory.createBox
     */
    WorldObjectType[WorldObjectType["BoxObject"] = 0] = "BoxObject";
    /**
     * A sphere object created with IObjectFactory.createSphere
     */
    WorldObjectType[WorldObjectType["SphereObject"] = 1] = "SphereObject";
    /**
     * A mesh object created with IObjectFactory.createRawMeshObject with an IPointMesh as the mesh
     */
    WorldObjectType[WorldObjectType["PointObject"] = 2] = "PointObject";
    /**
     * A mesh object created with IObjectFactory.createRawMeshObject with an ILineStripMesh as the mesh
     */
    WorldObjectType[WorldObjectType["LineObject"] = 3] = "LineObject";
    /**
     * A mesh object created with IObjectFactory.createRawMeshObject with an ILineMesh as the mesh
     */
    WorldObjectType[WorldObjectType["LineSegmentObject"] = 4] = "LineSegmentObject";
    /**
     * A mesh object created with IObjectFactory.createRawMeshObject with an IPointMesh as the mesh
     */
    WorldObjectType[WorldObjectType["MeshObject"] = 5] = "MeshObject";
    /**
     * A tileset object created with IObjectFactory.createTileset
     */
    WorldObjectType[WorldObjectType["TilesetObject"] = 6] = "TilesetObject";
    /**
     * The world's camera object
     */
    WorldObjectType[WorldObjectType["CameraObject"] = 7] = "CameraObject";
    /**
     * An Obj file object created with IObjectFactory.createObj
     */
    WorldObjectType[WorldObjectType["ObjObject"] = 8] = "ObjObject";
    /**
     * An IFC file object created with IObjectFactory.createIfc
     */
    WorldObjectType[WorldObjectType["IfcObject"] = 9] = "IfcObject";
    /**
     * A KML file object created with IObjectFactory.createKml
     */
    WorldObjectType[WorldObjectType["KmlObject"] = 10] = "KmlObject";
    /**
     * A DXF file object created with IObjectFactory.createDxf
     */
    WorldObjectType[WorldObjectType["DxfObject"] = 11] = "DxfObject";
    /**
     * A Sprite object created with IObjectFactory.createSprite
     */
    WorldObjectType[WorldObjectType["SpriteObject"] = 12] = "SpriteObject";
    /**
     * A LabelCollection object created with IObjectFactory.createLabelCollection
     */
    WorldObjectType[WorldObjectType["LabelCollection"] = 13] = "LabelCollection";
    /**
     * An InstancedMesh object created with IObjectFactory.createRawInstancedMeshObject
     */
    WorldObjectType[WorldObjectType["InstancedMesh"] = 14] = "InstancedMesh";
})(WorldObjectType || (WorldObjectType = {}));
